import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { EVENT_YEAR, FINISH_AT } from "@/constants/event";
import { fetchEventYears } from "@/api/eventYearApi";
import { parseKstDateTime } from "@/utils/kstDate";

// 행사 종료 여부를 앱 전역에 공급한다.
//
// 기준 시각은 백오피스(행사 연도 > 행사 종료 시각 = ee_finish_at)에 설정된 값이다.
// 상수 FINISH_AT 은 API 응답이 도착하기 전 / 조회가 실패했을 때만 쓰는 임시 기준이고,
// DB 값이 오면 앞당기든 미루든 무조건 그 값으로 교체된다.
const FinishContext = createContext(false);

// setTimeout 의 최대 지연(약 24.8일). 이보다 먼 시각은 나눠서 다시 건다.
const MAX_TIMEOUT_MS = 2147483647;

export const FinishProvider = ({ children }) => {
  const [finishAt, setFinishAt] = useState(FINISH_AT);
  const [finish, setFinish] = useState(() => Date.now() >= FINISH_AT.getTime());
  const timerRef = useRef(null);

  // 백오피스에 설정된 종료 시각을 받아와 기준을 교체한다.
  // 값이 비어 있거나 조회에 실패하면 임시 기준을 그대로 둔다.
  useEffect(() => {
    let isMounted = true;

    fetchEventYears()
      .then((res) => {
        if (!isMounted || !res?.ok) return;

        const current = res.data.find(
          (item) => Number(item?.year) === EVENT_YEAR,
        );
        const parsed = parseKstDateTime(current?.finish_at);

        if (parsed) setFinishAt(parsed);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  // 기준 시각이 정해지거나 바뀔 때마다 처음부터 다시 판정한다.
  // 운영자가 종료 시각을 뒤로 미루면 finish 도 true -> false 로 따라 내려간다.
  useEffect(() => {
    const check = () => {
      const remain = finishAt.getTime() - Date.now();

      if (remain <= 0) {
        setFinish(true);
        return;
      }

      setFinish(false);
      timerRef.current = setTimeout(check, Math.min(remain, MAX_TIMEOUT_MS));
    };

    check();

    // 절전/백그라운드로 타이머가 밀렸다가 돌아오는 경우를 대비해 복귀 시 한 번 더 확인
    const handleVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [finishAt]);

  return (
    <FinishContext.Provider value={finish}>{children}</FinishContext.Provider>
  );
};

// 사용: const finish = useFinish();
export const useFinish = () => useContext(FinishContext);

// 종료 시각 이후에는 fallback 을 대신 렌더한다.
// 라우트 정의를 감싸기만 하므로 감싼 화면의 기존 코드는 그대로 유지된다.
// 사용: <FinishGuard fallback={<NotFound />}><RegisterLayout /></FinishGuard>
export const FinishGuard = ({ children, fallback = null }) => {
  const finish = useFinish();
  return finish ? fallback : children;
};

export default FinishProvider;
